import React from 'react';

const Privacy = () => {
  return (
    <>
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up">
          <h1 className="hero-title" style={{ fontSize: '2.25rem' }}>
            Privacy Policy
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1rem' }}>
            Last Updated: March 3, 2026
          </p>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="container-sm">
          <div style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.8 }}>

            <p>
              Welcome, and thank you for your interest in kNine, a product and service offering operated by
              Knock Knock LLC ("Company," "we," or "us"). This Privacy Policy describes how we collect, use,
              process, disclose, and safeguard information we obtain through knine.ai (the "Website") and any
              related kNine services (the "Services").
            </p>
            <p>
              This Policy is incorporated into and is subject to the kNine Terms of Use. Capitalized terms not
              defined in this Policy have the meaning given to them in the Terms of Use.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              1. Personal Information We Collect
            </h2>
            <p>
              For the purpose of this Policy, "Personal Information" means any information relating to an
              identified or identifiable individual. We obtain Personal Information relating to you from various
              sources described below.
            </p>
            <p>
              Where applicable, we indicate whether and why you must provide us with your Personal Information,
              as well as the consequences of failing to do so. If you do not provide Personal Information when
              requested, you may not be able to benefit from all features of our Website or Services if that
              information is necessary to provide you with access, or if we are legally required to collect it.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '24px', marginBottom: '8px' }}>
              A. Personal Information Provided by You
            </h3>
            <p>
              <strong>Account Registration.</strong> If you register to use the Website or Services, you may
              provide Personal Information such as your name, email address, password, and related account details.
              Some registrations may require a company name, billing details, and/or a physical address.
            </p>
            <p>
              <strong>User Content / Inputs.</strong> We may collect information you submit or upload through the
              Services (for example, search queries, prompts, lists, notes, contact/company information you input,
              or other content you provide).
            </p>
            <p>
              <strong>Communications.</strong> We collect information you provide through communications with us,
              such as when you submit forms, request support, schedule a demo, or participate in surveys.
            </p>

            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '24px', marginBottom: '8px' }}>
              B. Personal Information Collected via Automated Means
            </h3>
            <p>
              When you use the Website, we may collect information via cookies, web beacons, and similar technologies.
            </p>
            <p>
              <strong>Cookies and Similar Technologies.</strong> We may use session and persistent cookies to collect
              information about your device and usage of the Website. If you disable cookies, some features may not
              function properly.
            </p>
            <p>
              Automatically collected information may include: IP address, device identifiers, browser type, operating
              system, referring/exit pages, pages or content you view or interact with, and dates/times of access. We
              may also collect email interaction data (e.g., open/click activity) where applicable.
            </p>
            <p>
              We use this information to understand usage trends, improve performance, develop features, and enhance security.
            </p>
            <p>
              <strong>Google Analytics.</strong> The Website may use Google Analytics, which uses cookies and similar
              technologies to collect and analyze information about Website use and report on activities and trends. You
              can learn about Google's practices at Google's policies pages and opt out via Google's Analytics opt-out tools.
            </p>
            <p>
              <strong>Third Party Web Beacons and Buttons.</strong> The Website may include third-party content (such as
              share buttons) that may collect information via their own cookies. Such information is collected directly
              by the third party and is subject to that third party's privacy policies.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              2. How We Use Personal Information
            </h2>
            <p>We may use Personal Information we collect for the following purposes:</p>
            <p>
              <strong>Internal and Service-Related Usage.</strong> To operate, maintain, enhance, and provide the
              Website and Services; to create and manage accounts; to provide requested information; and to provide support.
            </p>
            <p>
              <strong>Analytics and Improving the Service.</strong> To understand and analyze usage trends and preferences,
              improve the Website and Services, and develop new products, features, and functionality.
            </p>
            <p>
              <strong>Communications.</strong> To contact you for administrative purposes (customer service, security
              notices, policy updates) and to send communications relating to kNine or third-party offerings we work
              with. You may opt out of promotional communications as described below.
            </p>
            <p>
              <strong>Tailored Content.</strong> To personalize user experience and provide relevant content, offers,
              or information.
            </p>
            <p>
              <strong>Aggregate / De-Identified Data.</strong> We may de-identify and aggregate information to monitor
              effectiveness, improve the Website/Services, and measure usage metrics.
            </p>
            <p>
              <strong>Legal and Security.</strong> To enforce Terms of Use, defend legal rights, comply with legal
              obligations, detect/prevent fraud, and protect safety and integrity of the Website/Services.
            </p>
            <p>
              <strong>Other Purposes.</strong> As described at the time of collection or with your consent.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              3. How We Process Payment Information
            </h2>
            <p>
              We may use third-party payment processors to handle payments. We do not retain complete credit card
              information on our servers. Payment information may be stored by the payment processor in accordance
              with their terms and policies. By submitting payment information, you agree to the processor's terms.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              4. How We Disclose Personal Information
            </h2>
            <p>
              Except as described in this Policy or disclosed to you at the time of collection, we do not disclose
              Personal Information to third parties without your consent. We may disclose information in the following
              circumstances:
            </p>
            <p>
              <strong>Vendors and Service Providers.</strong> We may share Personal Information with service providers
              who help us operate the Website/Services (hosting, analytics, customer support, payment processing,
              security). They may access or process Personal Information only as needed to perform services for us
              and are expected to protect it.
            </p>
            <p>
              <strong>In Aggregated / De-Identified Form.</strong> We may disclose aggregated or de-identified
              information for reporting, analytics, business, or marketing purposes.
            </p>
            <p>
              <strong>Legal Compliance.</strong> We may disclose information if required by law, subpoena, warrant,
              or in good faith belief that disclosure is necessary to comply with law or protect rights/safety.
            </p>
            <p>
              <strong>Protect and Enforce Rights.</strong> To prevent fraud or abuse, investigate claims, protect
              security, enforce agreements, and protect our legal rights and the rights/safety of others.
            </p>
            <p>
              <strong>Business Transfers.</strong> In connection with a merger, acquisition, financing, sale of assets,
              reorganization, bankruptcy, or similar transaction.
            </p>
            <p>
              <strong>With Your Consent.</strong> As described at collection or with your permission.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              5. Your Rights and Choices
            </h2>
            <p>
              <strong>Account Information.</strong> You may update, correct, or delete account profile information by
              accessing account settings (if available) or by contacting us.
            </p>
            <p>
              <strong>Opt-Out.</strong> You may unsubscribe from promotional emails using the link in the email or by
              contacting us. You may still receive administrative or service-related emails.
            </p>
            <p>
              <strong>Do Not Track.</strong> Our Website may not currently respond to "Do Not Track" browser signals.
            </p>
            <p>
              <strong>EEA Rights.</strong> If you are located in the European Economic Area, you may have additional
              rights (access, correction, deletion, restriction, objection, portability). Those rights may be limited
              by law.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              6. Third-Party Services
            </h2>
            <p>
              This Policy applies only to information processed by Knock Knock LLC for kNine. The Website may contain
              links to third-party services. We are not responsible for third-party privacy practices. Please review
              third-party policies before providing information.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              7. Children's Privacy
            </h2>
            <p>
              The Website is not directed to children under 13 and we do not knowingly collect Personal Information
              from children under 13 without parental consent. If we learn such information was collected, we will
              delete it.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              8. Data Security
            </h2>
            <p>
              We use physical, managerial, and technical safeguards designed to protect Personal Information. HOWEVER,
              NO METHOD OF TRANSMISSION OR STORAGE IS 100% SECURE. YOU TRANSMIT INFORMATION AT YOUR OWN RISK. We
              cannot guarantee protection against every possible breach.
            </p>
            <p>
              If we learn of a security breach, we may notify you electronically and/or post notice on the Website.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              9. Data Retention
            </h2>
            <p>
              We retain Personal Information as necessary to provide the Website/Services, comply with legal obligations,
              resolve disputes, enforce agreements, and for legitimate business purposes. We take measures to delete or
              de-identify information when no longer needed, unless retention is required by law.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              10. Data Transfers
            </h2>
            <p>
              The Website is hosted in the United States and is intended only for visitors located within the United
              States. Users outside the United States are not eligible to use our Website or Services. If you are
              located outside the United States, please do not upload or enter personal information to the Website.
            </p>
            <p>
              If you choose to use the Website from outside the United States, you understand that information may be
              transferred to and processed in the United States.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              11. Changes and Updates to this Policy
            </h2>
            <p>
              We may update this Policy from time to time. We will post the updated Policy on the Website and revise
              the "Last Updated" date. Continued use after changes become effective indicates acceptance of the updated Policy.
            </p>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginTop: '32px', marginBottom: '12px' }}>
              12. How to Contact Us
            </h2>
            <p>
              Knock Knock LLC is the entity responsible for processing Personal Information as described in this Policy.
            </p>
            <p>
              If you have any questions or comments about this Policy, contact us at{' '}
              <a href="mailto:info@knockknock.ai">info@knockknock.ai</a> or write to us at:
            </p>
            <p style={{ paddingLeft: '24px', fontStyle: 'italic' }}>
              Knock Knock LLC<br />
              11370 Butternut Road<br />
              Chardon, OH 44024<br />
              United States of America
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Privacy;
